import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateQuote } from '../../hooks/useCreateQuote';
import { RouteDetailsSection } from '../../components/user/RouteDetailsSection';
import { DateSelectionSection } from '../../components/user/DateSelectionSection';
import { CargoDetailsSection } from '../../components/user/CargoDetailsSection';
import { AdditionalInfoSection } from '../../components/user/AdditionalInfoSection';
import { TransportationMethodsSection } from '../../components/user/TransportationMethodsSection';
import { ContactInfoSection } from '../../components/user/ContactInfoSection';
import { FormErrorBanner } from '../../components/shared/FormErrorBanner';
import { TermsAcceptance } from '../../components/shared/TermsAcceptance';
import { legalDocumentPathForUiLocale, normalizeUiLocale } from '../../legal/documents';

const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const labelClass = "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block";

export const CreateQuotePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const uiLocale = normalizeUiLocale(i18n?.resolvedLanguage || i18n?.language);
  const {
    formData, handleChange, handleCountryChange, handleFileChange, handleRemoveFile,
    handleDismissRejectedFiles, handleSubmit,
    termsAccepted, termsError, termsStale, termsAlreadyAccepted, handleTermsChange,
    files, hasRejectedFiles, loading, fieldErrors, formErrors
  } = useCreateQuote();

  return (
    <div className="max-w-4xl mx-auto p-0 lg:p-6">
      <div className="rounded-lg border border-border bg-card text-card-foreground shadow-sm">
        <div className="flex flex-col space-y-1.5 p-6">
          <h3 className="text-2xl leading-none tracking-tight">{t('quote.form.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('quote.form.subtitle')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('quote.form.mandatory_hint')}</p>
        </div>

        <div className="p-6 pt-0">
          <form onSubmit={handleSubmit} className="space-y-8" noValidate>
            <RouteDetailsSection
              formData={formData}
              handleChange={handleChange}
              handleCountryChange={handleCountryChange}
              fieldErrors={fieldErrors.route}
              inputClass={inputClass}
              labelClass={labelClass}
            />

            <DateSelectionSection
              formData={formData}
              handleChange={handleChange}
              handleCountryChange={handleCountryChange}
              inputClass={inputClass}
              labelClass={labelClass}
            />

            <CargoDetailsSection
              formData={formData}
              handleChange={handleChange}
              handleFileChange={handleFileChange}
              fieldErrors={fieldErrors.cargo}
              inputClass={inputClass}
              labelClass={labelClass}
            />

            <AdditionalInfoSection
              formData={formData}
              handleChange={handleChange}
              handleFileChange={handleFileChange}
              onRemoveFile={handleRemoveFile}
              onDismissRejectedFiles={handleDismissRejectedFiles}
              hasRejectedFiles={hasRejectedFiles}
              files={files}
              cargoImageErrors={fieldErrors.cargo_images}
              inputClass={inputClass}
              labelClass={labelClass}
            />

            <TransportationMethodsSection
              formData={formData}
              handleChange={handleChange}
              labelClass={labelClass}
            />

            {/* Keep the legal notice and Terms control close together. */}
            <div className="space-y-4">
              <ContactInfoSection
                formData={formData}
                handleChange={handleChange}
                fieldErrors={fieldErrors.contact}
                inputClass={inputClass}
                labelClass={labelClass}
              />

              {!termsAlreadyAccepted && <TermsAcceptance
                idPrefix="quote"
                checked={termsAccepted}
                onChange={handleTermsChange}
                error={termsError}
                stale={termsStale}
              />}
            </div>

            <FormErrorBanner errors={formErrors} />

            {termsAlreadyAccepted && <p className="text-sm text-muted-foreground">
              {t('legal.terms_acceptance.submit_notice_before')}<a href={legalDocumentPathForUiLocale('terms', uiLocale)} target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline underline-offset-2">{t('legal.terms')}</a>{t('legal.terms_acceptance.submit_notice_after')} {t('legal.terms_acceptance.privacy_notice_before')}<a href={legalDocumentPathForUiLocale('privacy', uiLocale)} target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline underline-offset-2">{t('legal.privacy')}</a>{t('legal.terms_acceptance.privacy_notice_after')}
            </p>}
            <button
              type="submit"
              disabled={loading}
              className="!mt-4 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 w-full"
            >
              {loading ? t('quote.form.buttons.submitting') : t('quote.form.buttons.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
